import { forwardRef } from "react";
import {
  TextField,
  FormControl,
  FormHelperText,
  MenuItem,
  IconButton,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useField, useFormikContext } from "formik";
import moment from "moment";
import DatePicker from "react-datepicker";
import Select from "react-select";

export function TextInput(props) {
  const [field, meta] = useField(props);
  return (
    <TextField
      size="small"
      fullWidth
      error={meta.touched && meta.error}
      {...field}
      {...props}
      helperText={meta.error}
    />
  );
}

export function BasicSelect(props) {
  const [field, meta] = useField(props);
  return (
    <FormControl fullWidth {...props} error={meta.touch && meta.error}>
      <TextField
        size="small"
        error={meta.touch && meta.error}
        select
        {...field}
        {...props}
      >
        {props.options.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </TextField>
      <FormHelperText>{meta.error}</FormHelperText>
    </FormControl>
  );
}

export function SearchSelect(props) {
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();
  return (
    <FormControl fullWidth {...props} error={meta.touch && meta.error}>
      <Select
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={document.body}
        isClearable={true}
        isSearchable={true}
        placeholder={props.label}
        {...field}
        {...props}
        onChange={(val) => {
          setFieldValue(field.name, val);
        }}
      />
      <FormHelperText>{meta.error}</FormHelperText>
    </FormControl>
  );
}

export function AutoCompleteSelect(props) {
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();
  return (
    <FormControl fullWidth {...props} error={meta.touch && meta.error}>
      <Select
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={document.body}
        isClearable={true}
        isSearchable={true}
        placeholder={props.label}
        {...field}
        {...props}
        onInputChange={(val) => {
          if (val.length > 1) {
            props.autoComplete(val);
          }
        }}
        onChange={(val) => {
          setFieldValue(field.name, val);
        }}
      />
      <FormHelperText>{meta.error}</FormHelperText>
    </FormControl>
  );
}

const CustomInputMonthAndYear = forwardRef(
  ({ value, onClick, ...props }, ref) => {
    return (
      <TextField
        {...props}
        value={moment(value).format("MMM - YYYY")}
        size="small"
        fullWidth
        onClick={onClick}
        ref={ref}
      />
    );
  }
);

export function MonthAndYearPicker(props) {
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();
  return (
    <FormControl fullWidth {...props} error={meta.touch && meta.error}>
      <DatePicker
        showMonthYearPicker
        withPortal
        {...field}
        {...props}
        selected={(field.value && new Date(field.value)) || null}
        onChange={(val) => {
          setFieldValue(field.name, val);
        }}
        customInput={<CustomInputMonthAndYear label={props.label} />}
      />
      <FormHelperText>{meta.error}</FormHelperText>
    </FormControl>
  );
}

export function DateInput(props) {
  const [field, meta] = useField(props);
  return (
    <FormControl fullWidth {...props} error={meta.touch && meta.error}>
      <TextField
        type="date"
        size="small"
        {...field}
        {...props}
        defaultValue={field.value}
        fullWidth
        InputLabelProps={{
          shrink: true,
        }}
      />
      <FormHelperText>{meta.error}</FormHelperText>
    </FormControl>
  );
}

export function CloseButton({ onClick }) {
  return (
    <IconButton onClick={onClick} aria-label="close">
      <CloseIcon />
    </IconButton>
  );
}

export function TextInputPercentage(props) {
  const [field, meta] = useField(props);
  return (
    <TextField
      size="small"
      fullWidth
      InputProps={{
        endAdornment: <InputAdornment position="start">%</InputAdornment>,
      }}
      error={meta.touched && meta.error}
      {...field}
      {...props}
      helperText={meta.error}
    />
  );
}
